import { useQuery } from '@tanstack/react-query';

export type BillingResourceLoader = {
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
};

/**
 * Drives a billing resource load (plan catalog, `initialize`, …) through TanStack Query, exposing a
 * loading/error/retry state. The billing service holds the loaded data in its own atoms (read via
 * `useStore`); this query only orchestrates the fetch and its lifecycle. Re-runs when `queryKey`
 * changes (e.g. a plan query param). Shared by the available-plans and checkout data hooks.
 */
export function useBillingResourceLoader(
  queryKey: readonly unknown[],
  load: () => Promise<unknown>,
  /**
   * Set to false to stop the load. A refresh does not use the cache that prevents rate limits from the
   * billing vendor, so a page that is about to redirect must not ask for data.
   */
  enabled = true,
): BillingResourceLoader {
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      await load();
      return true;
    },
    enabled,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    // A query that is off stays pending, and that reads as "still busy".
    isLoading: enabled && query.isPending,
    isError: query.isError,
    retry: () => void query.refetch(),
  };
}
